import {LAYER_TYPES, LayerOptions} from '../../../../../shared/image-layers/layer.types';
import {BaseLayer} from '../../../../../shared/image-layers/layers/BaseLayer';
import {action, observable} from 'mobx';
import {ICON_PATHS} from '../../icons';

const BASE_FONT_SIZE = 12;
const BASE_ICON_SIZE = 16;
const BASE_ICON_TEXT_GAP = 10;
const BASE_ROW_GAP = 8;
const BASE_SECTION_PADDING_X = 10;
const BASE_SECTION_PADDING_Y = 8;
const BASE_BORDER_RADIUS = 10;
const BASE_RSSI_BAR_HEIGHT = 36;
const BASE_RSSI_BAR_WIDTH = 400;
const BASE_RSSI_BOTTOM_PADDING = 10;
const BASE_GAP_ABOVE_RSSI = 10;

const MAX_TEXT_LENGTH = 20;

const BOX_BACKGROUND = 'rgba(0, 0, 0, 0.5)';
const BOX_BORDER_COLOR = 'rgba(144, 202, 249, 0.5)';
const BOX_BORDER_WIDTH = 1;
const TEXT_COLOR = '#FFFFFF';
const ICON_COLOR = 'rgba(144, 202, 249, 1)';
const DIVIDER_COLOR = 'rgba(144, 202, 249, 0.5)';
const SELECTED_BG = 'rgba(30, 153, 253, 0.3)';
const SELECTED_BORDER_COLOR = 'rgba(30, 153, 253, 0.5)';

interface ScaledSizes {
    fontSize: number;
    iconSize: number;
    iconTextGap: number;
    rowGap: number;
    sectionPaddingX: number;
    sectionPaddingY: number;
    borderRadius: number;
}

export interface AccessPointBand {
    frequency?: string;
    bssid: string;
}

export interface AccessPointData {
    ssid: string;
    bands: AccessPointBand[];
}

export interface AccessPointsInfoLayerOptions extends LayerOptions {
    id?: string;
    bottomOffset?: number;
    headerText?: string;
}

export class AccessPointsInfoLayer extends BaseLayer {
    @observable public accessPoints: AccessPointData[] = [];
    @observable public selectedIndex: number = 0;

    private readonly bottomOffset: number;
    private readonly headerText: string;

    constructor(options?: AccessPointsInfoLayerOptions) {
        super(options?.id ?? 'access-points-info', LAYER_TYPES.ACCESS_POINTS_INFO, {
            visible: options?.visible ?? true,
            opacity: options?.opacity ?? 1,
            zIndex: options?.zIndex ?? 3
        });

        this.bottomOffset = options?.bottomOffset ?? 56;
        this.headerText = options?.headerText ?? 'Access Points';
    }

    @action
    setAccessPoints(accessPoints: AccessPointData[], selectedIndex: number = 0): void {
        this.accessPoints = accessPoints;
        this.selectedIndex = selectedIndex;
        this.invalidate();
    }

    @action
    setSelectedIndex(index: number): void {
        this.selectedIndex = index;
        this.invalidate();
    }

    private truncateText(text: string): string {
        return text.length > MAX_TEXT_LENGTH ? text.slice(0, MAX_TEXT_LENGTH) + '...' : text;
    }

    private getScaledSizes(scaleFactor: number): ScaledSizes {
        return {
            fontSize: Math.round(BASE_FONT_SIZE * scaleFactor),
            iconSize: Math.round(BASE_ICON_SIZE * scaleFactor),
            iconTextGap: Math.round(BASE_ICON_TEXT_GAP * scaleFactor),
            rowGap: Math.round(BASE_ROW_GAP * scaleFactor),
            sectionPaddingX: Math.round(BASE_SECTION_PADDING_X * scaleFactor),
            sectionPaddingY: Math.round(BASE_SECTION_PADDING_Y * scaleFactor),
            borderRadius: Math.round(BASE_BORDER_RADIUS * scaleFactor)
        };
    }

    render(
        ctx: CanvasRenderingContext2D,
        containerWidth: number,
        containerHeight: number,
        renderOptions?: Record<string, any>
    ): void {
        if (this.accessPoints.length === 0) return;

        const isMergeMode = renderOptions?.widthRatio !== undefined;
        const scaleFactor = isMergeMode
            ? (containerWidth * renderOptions!.widthRatio) / (renderOptions!.baseWidth || 350)
            : 1;

        const s = this.getScaledSizes(scaleFactor);

        const boxWidth = this.calculateBoxWidth(ctx, s);
        const boxHeight = this.calculateBoxHeight(s);

        let rssiBarX: number;
        let rssiBarWidth: number;
        let rssiBarY: number;
        const gap = Math.round(BASE_GAP_ABOVE_RSSI * scaleFactor);

        if (isMergeMode) {
            rssiBarWidth = Math.round(containerWidth * renderOptions!.widthRatio);
            const rssiBarHeight = Math.round(BASE_RSSI_BAR_HEIGHT * scaleFactor);
            const mergeBottomPadding = renderOptions!.bottomPadding ?? 20;
            const centerOffset = renderOptions!.centerOffset ?? 0;
            rssiBarX = Math.round((containerWidth - rssiBarWidth) / 2) + centerOffset;
            rssiBarY = containerHeight - rssiBarHeight - mergeBottomPadding;
        } else {
            rssiBarWidth = BASE_RSSI_BAR_WIDTH;
            const rssiBarHeight = BASE_RSSI_BAR_HEIGHT;
            rssiBarX = Math.round((containerWidth - rssiBarWidth) / 2);
            rssiBarY = containerHeight - rssiBarHeight - BASE_RSSI_BOTTOM_PADDING;
        }

        const x = rssiBarX + rssiBarWidth - boxWidth;
        const y = rssiBarY - gap - boxHeight;

        this.renderOuterBox(ctx, x, y, boxWidth, boxHeight, s);

        let currentY = y;

        currentY = this.renderHeader(ctx, x, currentY, boxWidth, s);
        this.renderDivider(ctx, x, currentY, boxWidth, s);

        this.accessPoints.forEach((ap, index) => {
            const isSelected = index === this.selectedIndex;

            currentY = this.renderAccessPointRow(ctx, x, currentY, boxWidth, ap.ssid, isSelected, s);

            ap.bands.forEach((band) => {
                currentY = this.renderBandSection(ctx, x, currentY, boxWidth, band, s);
            });

            if (index < this.accessPoints.length - 1) {
                this.renderDivider(ctx, x, currentY, boxWidth, s);
            }
        });
    }

    private calculateBoxWidth(ctx: CanvasRenderingContext2D, s: ScaledSizes): number {
        ctx.font = `600 ${s.fontSize}px "Open Sans", sans-serif`;

        let maxContentWidth = ctx.measureText(this.headerText).width;

        this.accessPoints.forEach((ap) => {
            const ssidWidth = s.iconSize + s.iconTextGap + ctx.measureText(this.truncateText(ap.ssid)).width;
            maxContentWidth = Math.max(maxContentWidth, ssidWidth);

            ap.bands.forEach((band) => {
                if (band.frequency) {
                    const freqWidth =
                        s.iconSize + s.iconTextGap + ctx.measureText(this.truncateText(band.frequency)).width;
                    maxContentWidth = Math.max(maxContentWidth, freqWidth);
                }
                const bssidWidth = s.iconSize + s.iconTextGap + ctx.measureText(this.truncateText(band.bssid)).width;
                maxContentWidth = Math.max(maxContentWidth, bssidWidth);
            });
        });

        return maxContentWidth + s.sectionPaddingX * 2;
    }

    private getBandRowCount(band: AccessPointBand): number {
        return band.frequency ? 2 : 1;
    }

    private calculateBoxHeight(s: ScaledSizes): number {
        const headerHeight = s.sectionPaddingY * 2 + s.fontSize;
        let totalHeight = headerHeight;

        this.accessPoints.forEach((ap) => {
            totalHeight += s.sectionPaddingY * 2 + s.fontSize;
            ap.bands.forEach((band) => {
                const rowCount = this.getBandRowCount(band);
                totalHeight += s.sectionPaddingY * 2 + rowCount * s.fontSize + (rowCount - 1) * s.rowGap;
            });
        });

        return totalHeight;
    }

    private renderOuterBox(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        s: ScaledSizes
    ): void {
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, s.borderRadius);
        ctx.fillStyle = BOX_BACKGROUND;
        ctx.fill();
        ctx.strokeStyle = BOX_BORDER_COLOR;
        ctx.lineWidth = BOX_BORDER_WIDTH;
        ctx.stroke();
        ctx.clip();
    }

    private renderHeader(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, s: ScaledSizes): number {
        const sectionHeight = s.sectionPaddingY * 2 + s.fontSize;

        ctx.fillStyle = TEXT_COLOR;
        ctx.font = `700 ${s.fontSize}px "Open Sans", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.headerText, x + width / 2, y + sectionHeight / 2);

        return y + sectionHeight;
    }

    private renderAccessPointRow(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        ssid: string,
        isSelected: boolean,
        s: ScaledSizes
    ): number {
        const sectionHeight = s.sectionPaddingY * 2 + s.fontSize;

        if (isSelected) {
            ctx.fillStyle = SELECTED_BG;
            ctx.fillRect(x, y, width, sectionHeight);

            ctx.strokeStyle = SELECTED_BORDER_COLOR;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, y + sectionHeight);
            ctx.lineTo(x + width, y + sectionHeight);
            ctx.stroke();
        }

        const iconX = x + s.sectionPaddingX;
        const centerY = y + sectionHeight / 2;

        this.drawWifiIcon(ctx, iconX, centerY, s.iconSize);

        ctx.fillStyle = TEXT_COLOR;
        ctx.font = `600 ${s.fontSize}px "Open Sans", sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.truncateText(ssid), iconX + s.iconSize + s.iconTextGap, centerY);

        return y + sectionHeight;
    }

    private renderBandSection(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        band: AccessPointBand,
        s: ScaledSizes
    ): number {
        const rowCount = this.getBandRowCount(band);
        const sectionHeight = s.sectionPaddingY * 2 + rowCount * s.fontSize + (rowCount - 1) * s.rowGap;
        const iconX = x + s.sectionPaddingX;

        let nextRowY = y + s.sectionPaddingY + s.fontSize / 2;

        if (band.frequency) {
            this.drawAttachedIcon(ctx, iconX, nextRowY, s.iconSize);

            ctx.fillStyle = TEXT_COLOR;
            ctx.font = `500 ${s.fontSize}px "Open Sans", sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(this.truncateText(band.frequency), iconX + s.iconSize + s.iconTextGap, nextRowY);

            nextRowY += s.fontSize + s.rowGap;
        }

        this.drawRouterIcon(ctx, iconX, nextRowY, s.iconSize);

        ctx.fillStyle = TEXT_COLOR;
        ctx.font = `600 ${s.fontSize}px "Open Sans", sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.truncateText(band.bssid), iconX + s.iconSize + s.iconTextGap, nextRowY);

        return y + sectionHeight;
    }

    private renderDivider(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, s: ScaledSizes): void {
        ctx.strokeStyle = DIVIDER_COLOR;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + width, y);
        ctx.stroke();
    }

    private drawSvgIcon(
        ctx: CanvasRenderingContext2D,
        pathData: string,
        x: number,
        centerY: number,
        size: number,
        viewBoxSize: number
    ): void {
        const scale = size / viewBoxSize;
        const offsetY = centerY - size / 2;

        ctx.save();
        ctx.translate(x, offsetY);
        ctx.scale(scale, scale);

        const path = new Path2D(pathData);
        ctx.fillStyle = ICON_COLOR;
        ctx.fill(path);
        ctx.restore();
    }

    private drawWifiIcon(ctx: CanvasRenderingContext2D, x: number, centerY: number, size: number): void {
        this.drawSvgIcon(ctx, ICON_PATHS.wifi.path, x, centerY, size, ICON_PATHS.wifi.viewBox);
    }

    private drawAttachedIcon(ctx: CanvasRenderingContext2D, x: number, centerY: number, size: number): void {
        this.drawSvgIcon(ctx, ICON_PATHS.attached.path, x, centerY, size, ICON_PATHS.attached.viewBox);
    }

    private drawRouterIcon(ctx: CanvasRenderingContext2D, x: number, centerY: number, size: number): void {
        this.drawSvgIcon(ctx, ICON_PATHS.router.path, x, centerY, size, ICON_PATHS.router.viewBox);
    }
}
